import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';
import { fetchCsrfToken } from '../../helper/CsrfTokenHelper';

const SLICE_NAME_USER_TOKEN = 'userToken';

export interface UserTokenState {
  readonly value: string | null;
  readonly timeoutId?: NodeJS.Timeout;
}

const initialState: UserTokenState = {
  value: null
};

/* 更新頻度 : 15分 */
const TOKEN_UPDATE_FREQUENCY = 1_000 * 60 * 15;

/* トークンリフレッシュのStartアクション */
const startAutoRefreshToken = createAsyncThunk<NodeJS.Timeout | undefined, void, { readonly state: UserTokenState }>(
  `${SLICE_NAME_USER_TOKEN}/START_AUTO_REFRESH_TOKEN`,
  async (_, { dispatch, getState }) => {
    const { timeoutId: oldTimeoutId } = getState();
    /* 既にTimeoutIDがある場合は,スキップする */
    if (oldTimeoutId) {
      return undefined;
    }

    const timeoutId = setInterval(async () => {
      const token = await fetchCsrfToken();
      dispatch(userTokenSlice.actions.updateToken(token));
    }, TOKEN_UPDATE_FREQUENCY);

    return timeoutId;
  }
);

/* トークンリフレッシュのEndアクション（基本呼び出されない） */
const endAutoRefreshToken = createAsyncThunk<void, void, { readonly state: UserTokenState }>(
  `${SLICE_NAME_USER_TOKEN}/END_AUTO_REFRESH_TOKEN`,
  async (_, { getState }) => {
    const timeoutId = getState().timeoutId;
    if (timeoutId) {
      clearInterval(timeoutId);
    }
  }
);

export const actions = { startAutoRefreshToken, endAutoRefreshToken } as const;

const userTokenSlice = createSlice({
  name: SLICE_NAME_USER_TOKEN,
  initialState,
  selectors: {
    selectToken: (state: UserTokenState) => state.value
  },
  reducers: {
    updateToken: (state, action: PayloadAction<string>) => {
      const value = action.payload;
      return { ...state, value };
    }
  },
  extraReducers: builder => {
    builder
      .addCase(startAutoRefreshToken.fulfilled, (state, action) => {
        if (action.payload) {
          state.timeoutId = action.payload;
        }
      })
      .addCase(endAutoRefreshToken.fulfilled, state => {
        state.timeoutId = undefined;
      });
  }
});

export const useToken = () => useSelector(userTokenSlice.selectors.selectToken);

export default userTokenSlice.reducer;
